import { calcRelocation, hsvToHexColorCode } from '@/util/color-util';
import { GadgetMakerBase } from './GadgetMakerBase';
import { IDomain, IGraphData } from '@/class/models/typed/gadget/basicGadget';
import { MaterialId } from '@/class/common/const/gadget-material-id';
import { calcOpCountHeatmapWidth } from '@/class/common/util/op-count-heatmap-util';
import localize from '@/class/common/localize/localize-util';
import { HEATMAP_OP_COUNT_DEFAULT_MAX } from '@/class/common/const/operation-count-anyalysis-const';

const HUE_ON_OP_COUNT_MIN = 240;   // ヒートマップの色相。操作回数下限時。
const HUE_ON_OP_COUNT_MAX = 0;     // ヒートマップの色相。操作回数上限時。
export const OP_COUNT_MIN = 0;    // ヒートマップ表示上の操作回数下限

/**
 * ガジェットの作成者。ヒートマップ用。
 */
export class GadgetMakerOpCountHeatmap extends GadgetMakerBase
{
  readonly HEATMAP_HEIGHT = 24;  // ヒートマップのY軸セル数
  readonly NO_DATA_COLOR = "#7b7b7bff"  // データが無い箇所の色

  private heatmapWidth : number;  // ヒートマップのX軸セル数
  private dimentionMaterialId : number; // ヒートマップのX軸ラベルの種類（月、日、曜日）

  constructor(gadget_datas: IGraphData[], materials: IDomain[])
  {
    super(gadget_datas, materials);

    // DIMENTIONマテリアルID検索。
    this.dimentionMaterialId = findDimention( materials );

    if( this.gadgetDatas && this.gadgetDatas.length > 0){
      // 0番目のデータのlabelを選択日付とみなす。
      let currnt_date = new Date( this.gadgetDatas[0].label );
      // ヒートマップ横幅セル数算出
      this.heatmapWidth = calcOpCountHeatmapWidth(this.dimentionMaterialId, currnt_date);
    }
  }

  /**
   * [abstract] Chart.jsオプションの生成。
   * @param src_option_obj 元になるオプション。新規設定を加えて新規オプションを返却する。
   */
  public generateOptionObj(src_option_obj: Readonly<any>) : any
  {
    let new_option_obj = { ...src_option_obj }; // copy

    new_option_obj.scales.xAxes[0].display = true;
    new_option_obj.scales.yAxes[0].display = true;

    new_option_obj.scales.yAxes[0].ticks.min = 0;
    new_option_obj.scales.yAxes[0].ticks.max = 24;
    new_option_obj.scales.yAxes[0].ticks.stepSize = 0;
    new_option_obj.scales.yAxes[0].ticks.display = true;

    // Y軸目盛りラベルコールバック
    new_option_obj.scales.yAxes[0].ticks.callback = (value: any, index: any, values: any[]) => {
      // 0〜24 を 00:00〜24:00形式にして表示。
      return convertYIndexToScaleLabel(value);
    };

    // 積み上げ棒グラフにする
    new_option_obj.scales.xAxes[0].stacked = true;
    new_option_obj.scales.yAxes[0].stacked = true;

    // ツールチップ
    new_option_obj.tooltips.display = true;
    new_option_obj.tooltips.callbacks = {
        title: (tooltip_items, data) => {
          // note: titleではtooltip_itemsは配列。
          // console.log(`tooltip_items=${JSON.stringify(tooltip_items)}`)
          const ix = tooltip_items[0].index;
          if( 0 <= ix && ix < this.gadgetDatas.length ){
            const iy = tooltip_items[0].datasetIndex;
            const gadget_data = this.gadgetDatas[ix];
            if( 0 <= iy && iy < gadget_data.heatmapLineCells.length ){
              const x_label = convertRawToDispalyLabel(this.dimentionMaterialId, gadget_data.label, true);
              return `${x_label} ${convertYIndexToScaleLabel(iy)}`; // X軸ラベルと時間表示
            }
          }
          return "-"; // データがない場合。
        },
        label: (tooltip_item, data) => {
          const ix = tooltip_item.index;
          if( ix < this.gadgetDatas.length ){
            const gadget_data = this.gadgetDatas[ix];
            const iy = tooltip_item.datasetIndex;
            if( iy < gadget_data.heatmapLineCells.length ){
              const cell_data = gadget_data.heatmapLineCells[iy];
              return '平均操作回数/1h：' + Math.floor(cell_data.value) + "回";
            }
          }
          return "-"; // データがない場合。
        }
    };

    return new_option_obj;
  }

  /**
   * Chart.jsデータセットの作成。
   */
  public generateDatasets() : any[]
  {
    let datasets = [];

    // データ行数取得
    const num_gadget_data = this.gadgetDatas.length;
    // エラーチェック
    if( num_gadget_data <= 0 ){
      console.warn("GadgetMakerOpCountHeatmap.generateDatasets() gadgetDatasのデータがありません。");
      return datasets;
    }

    // ヒートマップデータセット作成
    // note takezawa gadgetDatasは列データリスト、ヒートマップのdatasetsは行データリストなので変換する。
    for(let i=0; i<this.HEATMAP_HEIGHT; i++){
      let new_dataset = {
        label: `${i}:00 - ${i+1}:00`,
        data: new Array(this.heatmapWidth).fill(1),
        borderWidth: 0,
        borderHeight: 0,
        borderColor: "#FFFFFF",
        barPercentage: 1,
        categoryPercentage: 1,
      };

      const colorbar_value_max = this.getColorbarValueMax();
      let bg_colors: string[] = new Array();
      for(let k=0; k<this.heatmapWidth; ++k){
        // gadgetDatasデータがない場合
        if( k < this.gadgetDatas.length 
          && i < this.gadgetDatas[k].heatmapLineCells.length
        ){
          // データがある場合
          // 操作回数を色に変換。
          const gadget_data = this.gadgetDatas[k];
          const op_count = gadget_data.heatmapLineCells[i].value;
          const color = clacValueToColor(op_count, OP_COUNT_MIN, colorbar_value_max );
          bg_colors.push( color );
        }else{
          // データがない場合
          bg_colors.push( this.NO_DATA_COLOR );
        }
      }
      new_dataset['backgroundColor'] = [...bg_colors];
      datasets.push(new_dataset);
    }

    return datasets;
  }

  /**
   * ラベルリストの作成。
   */
  public generateLabels() : any
  {
    let labels = [];
    for(const gadget_data of this.gadgetDatas){
      const label = convertRawToDispalyLabel(this.dimentionMaterialId, gadget_data.label);
      labels.push( label );
    }
    return labels
  }

  /**
   * ヒートマップカラーバーの最大値算出。
   * @returns 
   */
  public getColorbarValueMax() : number
  {
    if( this.gadgetDatas && this.gadgetDatas.length > 0){
      return this.gadgetDatas[0].heatmapColorbarValueMax;
    }
    return HEATMAP_OP_COUNT_DEFAULT_MAX;
  }

} // end class

// ----------------------------------------------------------------------------
// ローカル関数。
// ----------------------------------------------------------------------------
/**
 * DIMENTIONのマテリアルID検索。
 * @param materials 
 * @returns 
 */
function findDimention(materials: IDomain[]) : number
{
  if( !materials ){
    console.error('materials is empty.');
    return undefined;
  }

  for(const material of materials){
    switch( material.id ){
    case MaterialId.DIMENSION_MONTH:
    case MaterialId.DIMENSION_DATE:
    case MaterialId.DIMENSION_DAY_OF_WEEK:
      return material.id;
    }
  }
  console.error('ヒートマップに対応するDIMENTIONが見つかりません。');
  return undefined;
}

/**
 * バックエンドから送信されたGadgetDatas[].labelをグラフ表示用のラベルに変換。
 * @param dimention_material_id 
 * @param gadget_data_label GadgetDatas[].label（日付または曜日番号）
 * @param is_add_day_of_week 曜日を追加するか。 
 */
function convertRawToDispalyLabel(dimention_material_id: number, gadget_data_label: string, is_add_day_of_week: boolean=false) : string
{
  let label: string;
  switch( dimention_material_id ){
  case MaterialId.DIMENSION_MONTH:
    {
      // gadgetDatas[].labelは日付が設定されているので、月を抽出して月文字列に変換。
      const date = new Date(gadget_data_label);
      label = localize.getShortMonthString(date.getMonth());
      // 曜日の追加
      if( is_add_day_of_week ) label += localize.getShortDayOfWeekStringParen(date.getDay());
    }
    break;
  case MaterialId.DIMENSION_DATE:
    {
      // gadgetDatas[].labelは日付が設定されているので、日を抽出して日文字列に変換。
      const date = new Date(gadget_data_label);
      label = localize.getDayString(date.getDate());
      // 曜日の追加
      if( is_add_day_of_week ) label += localize.getShortDayOfWeekStringParen(date.getDay());
    }
    break;
  case MaterialId.DIMENSION_DAY_OF_WEEK:
    // gadgetDatas[].labelは曜日番号が設定されているので、曜日文字列に変換。
    {
      const day_of_week = Number(gadget_data_label);
      label = localize.getShortDayOfWeekString(day_of_week);
    }
    break;
  default:
    console.error(`ヒートマップに対応するdimentionMaterialId(${dimention_material_id})ではありません。`);
    label = "-";
    break;
  }
  return label;
}

/**
 * Y軸セルインデックスををY軸目盛りラベルに変換。
 * 例:
 *  0     => 00:00
 *  12    => 12:00
 *  12.5  => 12:30
 * @param y_index Y軸インデックス。小数でも構わない。
 * @returns 
 */
function convertYIndexToScaleLabel(y_index: number) : string
{
  let hour_int = Math.trunc(y_index);
  let hour_str = hour_int.toString().padStart(2, '0');
  let minute_decimal = y_index - hour_int;
  let minute_str = (60 * minute_decimal).toString().slice(0, 2);
  minute_str = minute_str.padStart(2, '0');
  return hour_str + ':' + minute_str;
}

// ----------------------------------------------------------------------------
// グローバル関数。
// ----------------------------------------------------------------------------
/**
 * 操作回数から色に変換。
 * @param value 
 * @param min 
 * @param max 
 * @returns 
 */
export function clacValueToColor(value: number, min: number, max: number) : string {
  const hue = calcRelocation( value, min, max, HUE_ON_OP_COUNT_MIN, HUE_ON_OP_COUNT_MAX);
  return hsvToHexColorCode(hue, 1, 1);
}
